import { DotsThree, Plus, Robot } from "@phosphor-icons/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { CatalogThinker } from "~/api/aiAdminTypes";
import { SKILL_TEMPLATES } from "~/api/aiAdminTypes";
import {
  useAddThinkerToWorkspace,
  useDeleteThinker,
  useImportPersonas,
  useRemoveThinkerFromWorkspace,
  useSetThinkerBinding,
  useThinkerCatalog,
  useThinkerCatalogStatus,
} from "~/api/queries";
import { Pill, Row, Rows } from "~/features/automation/components";
import { Select } from "~/features/settings/controls";
import { skillTemplateLabel } from "~/features/settings/sections/thinkers/labels";
import { PersonaDialog } from "~/features/settings/sections/thinkers/PersonaDialog";
import { ShareThinkerDialog } from "~/features/settings/sections/thinkers/ShareThinkerDialog";
import type { ThinkerPanel } from "~/features/settings/sections/thinkers/ThinkerTabs";
import { Button } from "~/foundation/components/Button";
import { ConfirmDialog } from "~/foundation/components/Dialog";
import { Empty } from "~/foundation/components/Empty";
import { IconButton } from "~/foundation/components/IconButton";
import { Input } from "~/foundation/components/Input";
import { Menu, type MenuItem } from "~/foundation/components/Menu";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";

/** The engines a persona can be pinned to. Local models are managed on their own tab. */
const CLOUD_PROVIDERS = ["openai", "anthropic"] as const;

/**
 * The engine a persona runs on **for this workspace**. Three fields, one save: provider, model,
 * and the skill template that decides which tools it may reach for. Blank means the server
 * default, and clearing back to blank is a real action — it is how a workspace stops overriding.
 */
function BindingEditor({ thinker }: { thinker: CatalogThinker }) {
  const { t } = useTranslation();
  const setBinding = useSetThinkerBinding();
  const [provider, setProvider] = useState(thinker.provider ?? "");
  const [model, setModel] = useState(thinker.model ?? "");
  const [skillTemplate, setSkillTemplate] = useState(thinker.skillTemplate ?? "");

  const dirty =
    provider !== (thinker.provider ?? "") ||
    model !== (thinker.model ?? "") ||
    skillTemplate !== (thinker.skillTemplate ?? "");

  return (
    <div className="mt-2 flex flex-wrap items-end gap-2">
      <Select
        label={t("Engine")}
        value={provider}
        onChange={(event) => setProvider(event.target.value)}
      >
        <option value="">{t("Server default")}</option>
        {CLOUD_PROVIDERS.map((option) => (
          <option key={option} value={option}>
            {option === "openai" ? "OpenAI" : "Anthropic"}
          </option>
        ))}
      </Select>
      <Input
        label={t("Model")}
        value={model}
        placeholder={t("e.g. gpt-4o")}
        className="max-w-44"
        onChange={(event) => setModel(event.target.value)}
      />
      <Select
        label={t("Skills")}
        value={skillTemplate}
        onChange={(event) => setSkillTemplate(event.target.value)}
      >
        <option value="">{t("Default")}</option>
        {SKILL_TEMPLATES.map((template) => (
          <option key={template} value={template}>
            {skillTemplateLabel(template, t)}
          </option>
        ))}
      </Select>
      <Button
        size="sm"
        disabled={!dirty || setBinding.isPending}
        onClick={() =>
          setBinding.mutate(
            {
              id: thinker.id,
              binding: {
                provider: provider || null,
                model: model || null,
                skillTemplate: skillTemplate || null,
              },
            },
            { onSuccess: () => toast.success(t("Engine updated")) },
          )
        }
      >
        {t("Save engine")}
      </Button>
    </div>
  );
}

/**
 * Which personas this workspace can address, and on what.
 *
 * The catalog is global; a persona is only in this workspace's conversations once it has been
 * added here. That is why the empty state says "nothing is available until you add it" rather
 * than apologising — a workspace with no added personas is configured, not broken. The catalog's
 * own health is shown beside the count for the same reason: an empty picker and an outage look
 * identical otherwise.
 */
export function WorkspacePanel({
  canAdminister,
  openPersonaId = null,
  openPanel = null,
  onDialogClose,
}: {
  canAdminister: boolean;
  /** A deep link (`?persona=<id or name>`): open that persona's dialog on arrival. */
  openPersonaId?: string | null;
  /** …on this tab (`?panel=facets|hive|try`). */
  openPanel?: ThinkerPanel | null;
  /** The deep-linked dialog closed: the caller clears the URL parameter. */
  onDialogClose?: () => void;
}) {
  const { t } = useTranslation();
  const { data: catalog, isLoading } = useThinkerCatalog();
  const { data: status } = useThinkerCatalogStatus();
  const add = useAddThinkerToWorkspace();
  const remove = useRemoveThinkerFromWorkspace();
  const retire = useDeleteThinker();
  const seed = useImportPersonas();

  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<CatalogThinker | null>(null);
  const [creating, setCreating] = useState(false);
  const [sharing, setSharing] = useState<CatalogThinker | null>(null);
  const [retiring, setRetiring] = useState<CatalogThinker | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  // The deep link opens the dialog once the catalog is here — once per id, so closing it sticks.
  /** The open dialog came from the deep link: it may open on the linked tab. */
  const [fromLink, setFromLink] = useState(false);
  const opened = useRef<string | null>(null);
  useEffect(() => {
    if (!openPersonaId || !catalog || opened.current === openPersonaId) return;
    opened.current = openPersonaId;
    const found = catalog.find(
      (entry) =>
        entry.id === openPersonaId || entry.name.toLowerCase() === openPersonaId.toLowerCase(),
    );
    if (found) {
      setFromLink(true);
      setEditing(found);
    }
  }, [openPersonaId, catalog]);

  if (isLoading) {
    return <Spinner label={t("Loading")} />;
  }

  const query = search.trim().toLowerCase();
  const rows = (catalog ?? []).filter(
    (thinker) =>
      !query ||
      thinker.name.toLowerCase().includes(query) ||
      thinker.description.toLowerCase().includes(query),
  );
  const addedCount = (catalog ?? []).filter((thinker) => thinker.added).length;

  const menuFor = (thinker: CatalogThinker): MenuItem[] => {
    if (!canAdminister) {
      return [];
    }
    return [
      {
        type: "button",
        title: t("Edit"),
        onClick: () => {
          setFromLink(false);
          setEditing(thinker);
        },
      },
      ...(thinker.added
        ? ([
            { type: "button", title: t("Share"), onClick: () => setSharing(thinker) },
          ] as MenuItem[])
        : []),
      {
        type: "button",
        title: thinker.added ? t("Configure engine") : t("Add to workspace"),
        onClick: () =>
          thinker.added
            ? setExpanded(expanded === thinker.id ? null : thinker.id)
            : add.mutate(
                { thinkerId: thinker.id },
                {
                  onSuccess: () => toast.success(t("Added {{name}}", { name: thinker.name })),
                },
              ),
      },
      ...(thinker.added
        ? ([
            {
              type: "button",
              title: t("Remove from workspace"),
              onClick: () =>
                remove.mutate(thinker.id, {
                  onSuccess: () => toast.success(t("Removed {{name}}", { name: thinker.name })),
                }),
            },
          ] as MenuItem[])
        : []),
      { type: "separator" },
      {
        type: "button",
        title: t("Retire thinker"),
        dangerous: true,
        onClick: () => setRetiring(thinker),
      },
    ];
  };

  return (
    <div className="flex flex-col gap-3">
      <Text variant="small">
        {t("Personas you add here appear in conversations. Nothing is available until you add it.")}
      </Text>

      <div className="flex flex-wrap items-end justify-between gap-2">
        <Input
          type="search"
          placeholder={t("Search thinkers")}
          value={search}
          className="min-w-56"
          onChange={(event) => setSearch(event.target.value)}
        />
        <div className="flex items-center gap-2">
          <Text variant="small">
            {status && status.status !== "ok"
              ? t("Catalog unavailable — {{error}}", { error: status.lastError ?? t("unknown") })
              : t("{{added}} of {{total}} added", {
                  added: addedCount,
                  total: catalog?.length ?? 0,
                })}
          </Text>
          {canAdminister ? (
            <>
              <Button
                size="sm"
                disabled={seed.isPending}
                onClick={() =>
                  seed.mutate(
                    { includeBuiltIns: true },
                    {
                      onSuccess: (result) =>
                        toast.success(
                          t("{{count}} built-in thinkers seeded", {
                            count: result.builtInsSeeded,
                          }),
                        ),
                    },
                  )
                }
              >
                {t("Seed built-ins")}
              </Button>
              <Button size="sm" variant="primary" icon={Plus} onClick={() => setCreating(true)}>
                {t("New thinker")}
              </Button>
            </>
          ) : null}
        </div>
      </div>

      {rows.length === 0 ? (
        <Empty
          icon={Robot}
          message={
            query
              ? t("No thinker matches that search.")
              : t("No thinkers are in the catalog yet. Seed the built-ins to start.")
          }
        />
      ) : (
        <Rows>
          {rows.map((thinker) => (
            <Row key={thinker.id} className="flex-col items-stretch">
              <div className="flex w-full items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-body text-text">{thinker.name}</p>
                  <p className="truncate text-small text-text-tertiary">{thinker.description}</p>
                </div>
                {thinker.category ? (
                  <span className="hidden text-small text-text-tertiary md:inline">
                    {thinker.category}
                  </span>
                ) : null}
                {thinker.added ? (
                  <Pill tone="success">{t("In this workspace")}</Pill>
                ) : (
                  <Pill>{t("Not added")}</Pill>
                )}
                {thinker.model ? <Pill tone="accent">{thinker.model}</Pill> : null}
                {canAdminister ? (
                  thinker.added ? (
                    <Menu
                      items={menuFor(thinker)}
                      align="end"
                      ariaLabel={t("Actions")}
                      trigger={<IconButton icon={DotsThree} aria-label={t("Actions")} size="sm" />}
                    />
                  ) : (
                    <Button
                      size="sm"
                      icon={Plus}
                      disabled={add.isPending}
                      onClick={() =>
                        add.mutate(
                          { thinkerId: thinker.id },
                          {
                            onSuccess: () =>
                              toast.success(t("Added {{name}}", { name: thinker.name })),
                          },
                        )
                      }
                    >
                      {t("Add")}
                    </Button>
                  )
                ) : null}
              </div>
              {canAdminister && thinker.added && expanded === thinker.id ? (
                <BindingEditor thinker={thinker} />
              ) : null}
            </Row>
          ))}
        </Rows>
      )}

      {creating ? <PersonaDialog persona={null} onClose={() => setCreating(false)} /> : null}
      {editing ? (
        <PersonaDialog
          persona={editing}
          initialPanel={fromLink ? openPanel : null}
          onClose={() => {
            setEditing(null);
            setFromLink(false);
            onDialogClose?.();
          }}
        />
      ) : null}
      {sharing ? (
        <ShareThinkerDialog
          thinkerId={sharing.id}
          thinkerName={sharing.name}
          onClose={() => setSharing(null)}
        />
      ) : null}
      {retiring ? (
        <ConfirmDialog
          open
          onOpenChange={(open) => !open && setRetiring(null)}
          title={t("Retire {{name}}?", { name: retiring.name })}
          // Soft on the server: past conversations keep resolving the author. Said plainly,
          // because "delete" that quietly keeps history is worse than "retire" that says so.
          description={t(
            "It leaves every picker and this workspace. Conversations it already took part in keep their history.",
          )}
          confirmLabel={t("Retire")}
          cancelLabel={t("Cancel")}
          closeLabel={t("Close")}
          dangerous
          busy={retire.isPending}
          onConfirm={() =>
            retire.mutate(retiring.id, {
              onSuccess: () => {
                toast.success(t("Thinker retired"));
                setRetiring(null);
              },
            })
          }
        />
      ) : null}
    </div>
  );
}
